import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme, type Theme } from '@mui/material/styles';
import { deviceStatusColor, getDeviceStatusColorMaps } from '../../theme/uiSemanticColors';
import {
  Box,
  Typography,
  GridLegacy as Grid,
  Paper,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
} from '@mui/material';
import { AppIconButton, AppListPage } from '../../components/design-system';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import {
  DevicesOther as TotalIcon,
  Inventory2Outlined as StockIcon,
  CheckCircleOutline as ActiveIcon,
  PersonOffOutlined as UnassignedIcon,
  Refresh as RefreshIcon,
  WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material';
import { opsDeviceHealthReconciliationService } from '../../services/OpsDeviceHealthReconciliationService';
import { opsDevicesService } from '../../services/OpsDevicesService';
import type { OpsFleetStats, OpsFleetStatsByCategory, OpsDeviceDetail, DeviceHealthReconciliationSummary } from '@cnapse/shared/schemas.js';
import { getDeviceCategoryLabel, isNonIotDeviceCategory, PRODUCT_NAMES } from '@cnapse/shared';

const IOT_STATUS_LABELS: Record<string, string> = {
  manufactured: 'Manufactured',
  'ready-to-provision': 'Ready to Provision',
  provisioned: 'Provisioned',
  decommissioned: 'Decommissioned',
};

const NON_IOT_STATUS_LABELS: Record<string, string> = {
  'in-stock': 'In Stock', allocated: 'Allocated', shipped: 'Shipped',
  claimed: 'Claimed', active: 'Active', returned: 'Returned', retired: 'Retired',
};

interface StatCardProps {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
  subtitle?: string;
}

function StatCard({ label, value, color, icon, subtitle }: StatCardProps) {
  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Box sx={{ color }}>{icon}</Box>
        </Box>
        <Typography variant="h3" fontWeight="bold" sx={{ color }}>
          {value.toLocaleString()}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

const CATEGORY_META: { key: string; label: string; isIot: boolean }[] = [
  { key: 'efem-gateway', label: getDeviceCategoryLabel('efem-gateway'), isIot: true },
  { key: 'location-gateway', label: getDeviceCategoryLabel('location-gateway'), isIot: true },
  { key: 'multi-gateway', label: getDeviceCategoryLabel('multi-gateway'), isIot: true },
  { key: 'location-tag', label: getDeviceCategoryLabel('location-tag'), isIot: false },
  { key: 'location-anchor', label: getDeviceCategoryLabel('location-anchor'), isIot: false },
  { key: 'barcode-scanner', label: getDeviceCategoryLabel('barcode-scanner'), isIot: false },
  { key: 'handheld-barcode-scanner', label: getDeviceCategoryLabel('handheld-barcode-scanner'), isIot: false },
  { key: 'waferid-camera', label: getDeviceCategoryLabel('waferid-camera'), isIot: false },
];

function getDeviceStatusLabel(device: OpsDeviceDetail): string {
  const isNonIot = isNonIotDeviceCategory(device.deviceCategory);
  if (isNonIot) return NON_IOT_STATUS_LABELS[device.inventoryStatus ?? 'in-stock'] ?? (device.inventoryStatus ?? 'in-stock');
  return IOT_STATUS_LABELS[device.iotProvisioningStatus ?? 'manufactured'] ?? (device.iotProvisioningStatus ?? 'manufactured');
}

function getDeviceStatusColor(
  device: OpsDeviceDetail,
  statusMaps: ReturnType<typeof getDeviceStatusColorMaps>,
  theme: Theme,
): string {
  const isNonIot = isNonIotDeviceCategory(device.deviceCategory);
  if (isNonIot) {
    return deviceStatusColor(
      statusMaps,
      device.inventoryStatus ?? 'in-stock',
      'nonIot',
      theme,
    );
  }
  return deviceStatusColor(
    statusMaps,
    device.iotProvisioningStatus ?? 'manufactured',
    'iot',
    theme,
  );
}

interface AttentionItem {
  device: OpsDeviceDetail;
  reason: string;
}

export default function FleetDashboard() {
  const theme = useTheme();
  const statusMaps = useMemo(() => getDeviceStatusColorMaps(theme), [theme]);
  const [stats, setStats] = useState<OpsFleetStats | null>(null);
  const [byCategory, setByCategory] = useState<OpsFleetStatsByCategory | null>(null);
  const [recentDevices, setRecentDevices] = useState<OpsDeviceDetail[]>([]);
  const [attentionItems, setAttentionItems] = useState<AttentionItem[]>([]);
  const [reconciliation, setReconciliation] = useState<DeviceHealthReconciliationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, recentRes, readyRes, allocatedRes, returnedRes, reconciliationRes] = await Promise.all([
        opsDevicesService.getFleetStats(),
        opsDevicesService.listDevices({ limit: 50 }),
        opsDevicesService.listDevices({ iotProvisioningStatus: 'not_provisioned', limit: 50 }),
        opsDevicesService.listDevices({ inventoryStatus: 'allocated', limit: 50 }),
        opsDevicesService.listDevices({ inventoryStatus: 'returned', limit: 50 }),
        opsDeviceHealthReconciliationService.getDeviceHealthReconciliation().catch(() => null),
      ]);

      setStats(statsRes.data.stats);
      setByCategory(statsRes.data.byCategory ?? null);

      // Sort by createdAt descending and take top 10
      const sorted = [...recentRes.data.devices].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
      setRecentDevices(sorted.slice(0, 10));

      // Build attention list
      const items: AttentionItem[] = [];
      const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

      // IoT: stuck in ready-to-provision > 30 days
      for (const d of readyRes.data.devices) {
        if (d.allocatedAt && new Date(d.allocatedAt) < thirtyDaysAgo) {
          items.push({ device: d, reason: 'Ready-to-provision for >30 days' });
        }
      }

      // Non-IoT: allocated > 14 days (ordered but not shipped)
      for (const d of allocatedRes.data.devices) {
        const ts = d.allocatedAt ?? d.createdAt;
        if (ts && new Date(ts) < fourteenDaysAgo) {
          items.push({ device: d, reason: 'Allocated for >14 days – not yet shipped' });
        }
      }

      // Non-IoT: devices in returned state (need restocking decision)
      for (const d of returnedRes.data.devices) {
        items.push({ device: d, reason: 'Returned – needs restocking or retirement' });
      }

      setAttentionItems(items);
      setReconciliation(reconciliationRes?.data ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load fleet data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const navigateTo = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void load()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [load, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  // Compute fleet-wide summary from byCategory
  const totalDevices = byCategory
    ? Object.values(byCategory).reduce((sum, c) => sum + c.total, 0)
    : (stats ? stats.total + (stats.allDevices?.total ?? 0) : 0);
  const totalActive = byCategory
    ? Object.values(byCategory).reduce((sum, c) => sum + c.active, 0)
    : 0;
  const totalInStock = byCategory
    ? Object.values(byCategory).reduce((sum, c) => sum + c.inStock, 0)
    : 0;
  const totalUnassigned = stats?.unassigned ?? 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppListPage>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Fleet-wide summary cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total Devices"
            value={totalDevices}
            color={statusMaps.health.total}
            icon={<TotalIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Active / Deployed"
            value={totalActive}
            color={statusMaps.health.healthy}
            icon={<ActiveIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="In Stock"
            value={totalInStock}
            color={statusMaps.health.total}
            icon={<StockIcon />}
            subtitle="Ready to allocate"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Unassigned"
            value={totalUnassigned}
            color={totalUnassigned > 0 ? statusMaps.health.degraded : statusMaps.health.unknown}
            icon={<UnassignedIcon />}
            subtitle="No tenant assigned"
          />
        </Grid>
      </Grid>

      {reconciliation && (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Runtime Health Reconciliation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inventory bindings vs edge-reported runtime across {PRODUCT_NAMES.AXON} peripherals and CCI Spatial site infrastructure.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={1}
                sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                onClick={() => navigateTo('/provider/ops/efem-peripherals')}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {PRODUCT_NAMES.AXON} Peripherals
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip label={`${reconciliation.efem.boundPeripherals} bound`} size="small" />
                  <Chip label={`${reconciliation.efem.peripheralsHealthy} healthy`} size="small" color="success" variant="outlined" />
                  {reconciliation.efem.peripheralsUnreachable > 0 && (
                    <Chip label={`${reconciliation.efem.peripheralsUnreachable} unreachable`} size="small" color="error" />
                  )}
                  {reconciliation.efem.peripheralsUnknown > 0 && (
                    <Chip label={`${reconciliation.efem.peripheralsUnknown} unknown`} size="small" color="warning" />
                  )}
                  {reconciliation.efem.gatewaysWithIssues > 0 && (
                    <Chip label={`${reconciliation.efem.gatewaysWithIssues} ${PRODUCT_NAMES.AXON} w/ issues`} size="small" color="warning" variant="outlined" />
                  )}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={1}
                sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                onClick={() => navigateTo('/provider/ops/site-anchor-health')}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  CCI Spatial Infrastructure
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip label={`${reconciliation.location.anchorsAssigned} Satellites assigned`} size="small" />
                  <Chip label={`${reconciliation.location.anchorsOnline} online`} size="small" color="success" variant="outlined" />
                  {reconciliation.location.anchorsOffline > 0 && (
                    <Chip label={`${reconciliation.location.anchorsOffline} offline`} size="small" color="error" />
                  )}
                  {reconciliation.location.anchorsUnassignedPoints > 0 && (
                    <Chip label={`${reconciliation.location.anchorsUnassignedPoints} unassigned points`} size="small" color="warning" />
                  )}
                  {reconciliation.location.tagsUnregisteredSeen > 0 && (
                    <Chip
                      label={`${reconciliation.location.tagsUnregisteredSeen} unregistered Nodes seen`}
                      size="small"
                      color="warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateTo('/provider/ops/site-tag-fleet');
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={1}
                sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                onClick={() => navigateTo('/provider/ops/unattested')}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Unattested Devices
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip label={`${reconciliation.unattested.openAttempts} open`} size="small" color={reconciliation.unattested.openAttempts > 0 ? 'warning' : 'default'} />
                  {reconciliation.unattested.openAttemptsLast24h > 0 && (
                    <Chip label={`${reconciliation.unattested.openAttemptsLast24h} last 24h`} size="small" color="error" variant="outlined" />
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Per-category stats grid */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom>
          Device Categories
        </Typography>
        {byCategory ? (
          <Grid container spacing={2}>
            {CATEGORY_META.map(({ key, label, isIot }) => {
              const cat = byCategory[key as keyof typeof byCategory];
              if (!cat || cat.total === 0) return null;
              const statusPills = isIot
                ? [
                    { label: 'Provisioned', value: cat.active, color: statusMaps.iot.provisioned },
                    { label: 'In Stock', value: cat.inStock, color: statusMaps.iot.manufactured },
                  ]
                : [
                    { label: 'Active', value: cat.active, color: statusMaps.nonIot.active },
                    { label: 'In Stock', value: cat.inStock, color: statusMaps.nonIot['in-stock'] },
                    ...(cat.allocated ? [{ label: 'Allocated', value: cat.allocated, color: statusMaps.nonIot.allocated }] : []),
                    ...(cat.shipped ? [{ label: 'Shipped', value: cat.shipped, color: statusMaps.nonIot.shipped }] : []),
                    ...(cat.returned ? [{ label: 'Returned', value: cat.returned, color: statusMaps.nonIot.returned }] : []),
                  ];
              return (
                <Grid key={key} item xs={12} sm={6} md={4} lg={3}>
                  <Card
                    elevation={1}
                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}
                    onClick={() => navigateTo(`/provider/devices/inventory?category=${key}`)}
                  >
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
                        <Chip label={isIot ? 'IoT' : 'Non-IoT'} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                      </Box>
                      <Typography variant="h4" fontWeight="bold">{cat.total}</Typography>
                      <Box display="flex" gap={1.5} mt={0.5} flexWrap="wrap">
                        {statusPills.filter((p) => p.value > 0).map((p) => (
                          <Tooltip key={p.label} title={p.label}>
                            <Typography variant="caption" sx={{ color: p.color, fontWeight: 600 }}>
                              {p.value} {p.label.toLowerCase()}
                            </Typography>
                          </Tooltip>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box display="flex" gap={1.5} flexWrap="wrap">
            {CATEGORY_META.map(({ key, label }) => (
              <Chip
                key={key}
                label={label}
                variant="outlined"
                clickable
                onClick={() => navigateTo(`/provider/devices/inventory?category=${key}`)}
              />
            ))}
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Recently Added */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recently Added
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {recentDevices.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No devices found.
              </Typography>
            ) : (
              <List disablePadding>
                {recentDevices.map((device) => {
                  const color = getDeviceStatusColor(device, statusMaps, theme);
                  const statusLabel = getDeviceStatusLabel(device);
                  return (
                    <ListItem
                      key={device.serialNumber}
                      disableGutters
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 1 }}
                      onClick={() => navigateTo(`/provider/devices/${device.serialNumber}`)}
                    >
                      <ListItemText
                        primary={device.serialNumber}
                        secondary={`${device.deviceModel || device.deviceCategory} • ${device.resolvedTenantName ?? 'Unassigned'} • ${device.createdAt ? new Date(device.createdAt).toLocaleDateString() : '—'}`}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={statusLabel}
                          size="small"
                          sx={{ bgcolor: color + '20', color, fontWeight: 600 }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Needs Attention */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="h6">Needs Attention</Typography>
              {attentionItems.length > 0 && (
                <Chip
                  label={attentionItems.length}
                  size="small"
                  color="warning"
                  icon={<WarningIcon />}
                />
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            {attentionItems.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No devices require attention.
              </Typography>
            ) : (
              <List disablePadding>
                {attentionItems.slice(0, 8).map(({ device, reason }) => (
                  <ListItem
                    key={device.serialNumber}
                    disableGutters
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 1 }}
                    onClick={() => navigateTo(`/provider/devices/${device.serialNumber}`)}
                  >
                    <ListItemText
                      primary={device.serialNumber}
                      secondary={reason}
                      secondaryTypographyProps={{ color: 'warning.main' }}
                    />
                  </ListItem>
                ))}
                {attentionItems.length > 8 && (
                  <ListItem disableGutters sx={{ px: 1 }}>
                    <Typography variant="body2" color="warning.main">
                      +{attentionItems.length - 8} more devices
                    </Typography>
                  </ListItem>
                )}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </AppListPage>
  );
}